import { PRIMARY_OUTLET, type ActivatedRouteSnapshot, type ViewTransitionInfo } from '@angular/router';
import { productByPath } from '../data/products.config';
import { canonicalPath } from './locale-path';

/** The path a snapshot tree resolves to, read down the primary outlet, without the language. */
function pathOf(root: ActivatedRouteSnapshot): string {
  const segments: string[] = [];
  let node: ActivatedRouteSnapshot | undefined = root;

  while (node) {
    segments.push(...node.url.map((segment) => segment.path));
    node = node.children.find((child) => child.outlet === PRIMARY_OUTLET);
  }

  return canonicalPath(`/${segments.join('/')}`);
}

const isHome = (path: string): boolean => path === '/';
const isProduct = (path: string): boolean => productByPath(path) !== undefined;

/**
 * Runs the view transition only where something actually morphs.
 *
 * The site's one shared element is a product's lockup, which the home page's
 * cards and the header's links take turns holding — so only a move between the
 * two is animated, and every other navigation keeps the plain, instant swap it
 * has always had rather than acquiring a site-wide cross-fade nobody asked for.
 *
 * Browsers without the View Transitions API never get here: the router checks
 * for `document.startViewTransition` first and navigates normally without it.
 */
export function morphProductLockups({ transition, from, to }: ViewTransitionInfo): void {
  const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  if (reduced) {
    transition.skipTransition();
    return;
  }

  const before = pathOf(from);
  const after = pathOf(to);
  const handover =
    (isHome(before) && isProduct(after)) || (isProduct(before) && isHome(after));

  if (!handover) transition.skipTransition();
}
